import { Timestamp, addDoc, collection } from 'firebase/firestore';
import { firestore } from '@/utils/firebase';

export const AUDIT_ACTIONS_COLLECTION = 'auditActions';

const toSerializableValue = (value) => {
	if (value == null) return null;
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return value;
	}
	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return String(value);
	}
};

const toUserPayload = (user) => ({
	userId: user?.userId ?? null,
	hubspotId: user?.hubspotId ?? null,
	email: user?.email ?? null,
	name: user?.name ?? null,
});

/**
 * Persists an action to the shared auditActions collection (shown on the Audit page).
 * One document per changed field. A create or delete with no changed fields writes one
 * document instead. Ask AI audits stay separate for now.
 * clientId and dealId are stored when the caller passes them (task events).
 */
export const logAuditActions = async ({
	actionType,
	performedBy,
	targetUser,
	changedFields = {},
	previousValues = {},
	clientId,
	dealId,
	fieldChanged = null,
	previousValue = null,
	newValue = null,
}) => {
	if (!actionType) return [];

	const fieldEntries = Object.entries(changedFields ?? {}).filter(
		([field]) => field !== 'lastUpdated' && field !== 'lastUpdatedBy',
	);

	const performedByPayload = toUserPayload(performedBy);
	const targetUserPayload = toUserPayload(targetUser);
	// Profile updates omit these; task events pass the task's client and deal.
	const baseDoc = {
		timestamp: Timestamp.now(),
		actionType,
		performedBy: performedByPayload,
		targetUser: targetUserPayload,
		...(clientId !== undefined && { clientId }),
		...(dealId !== undefined && { dealId }),
	};

	const writeAuditDoc = (fieldChange) =>
		addDoc(collection(firestore, AUDIT_ACTIONS_COLLECTION), {
			...baseDoc,
			fieldChanged: fieldChange.fieldChanged,
			previousValue: toSerializableValue(fieldChange.previousValue),
			newValue: toSerializableValue(fieldChange.newValue),
		});

	// Create/delete events have no changed fields, so they are a single document.
	// Updates with nothing changed do not write a row.
	if (fieldEntries.length === 0) {
		const isCreateOrDelete =
			actionType.endsWith('Created') || actionType.endsWith('Deleted');
		if (!isCreateOrDelete) return [];

		const docRef = await writeAuditDoc({ fieldChanged, previousValue, newValue });
		return [docRef];
	}

	const writes = fieldEntries.map(([changedField, changedValue]) =>
		writeAuditDoc({
			fieldChanged: changedField,
			previousValue: previousValues?.[changedField],
			newValue: changedValue,
		}),
	);

	return Promise.all(writes);
};
